import { Fragment, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Download, Loader2, Receipt, Search } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API } from "@/context/AuthContext";
import { inr } from "@/pages/Dashboard";

const PAGE = 100;
const dmy = (d) => (d ? d.split("-").reverse().join(".") : "");
const StatusTag = ({ s }) => (s ? <span className={`rounded-full px-2 py-0.5 text-[10px] whitespace-nowrap ${s === "Cancelled" ? "bg-amber-100 text-amber-800" : "bg-orange-100 text-orange-800"}`}>{s}</span> : null);

const Lines = ({ lines }) => (
  <tr><td colSpan={9} className="bg-slate-50 px-6 py-2">
    <table className="w-full text-[11px]" data-testid="sales-tx-lines">
      <thead><tr className="text-slate-500 uppercase text-[10px]">{["Item", "Material", "Description", "WBS", "Delivery", "Qty", "Rate", "Sales ex-tax", "Tax"].map((h, i) => <th key={h} className={`py-1 px-1 ${i > 4 ? "text-right" : "text-left"}`}>{h}</th>)}</tr></thead>
      <tbody>
        {lines.map((l) => (
          <tr key={l.billing_item} className="border-t border-slate-200">
            <td className="px-1 py-0.5 font-mono">{l.billing_item}</td><td className="px-1 font-mono">{l.material}</td>
            <td className="px-1 max-w-[280px] truncate" title={l.material_description}>{l.material_description}</td>
            <td className="px-1 font-mono">{l.wbs_element}</td><td className="px-1 font-mono">{l.delivery_no}</td>
            <td className="px-1 text-right tabular-nums">{l.qty} {l.unit}</td>
            <td className="px-1 text-right tabular-nums">{l.qty ? inr(l.sales_value / l.qty) : "—"}</td>
            <td className="px-1 text-right tabular-nums font-semibold">{inr(l.sales_value)}</td>
            <td className="px-1 text-right tabular-nums">{inr(l.tax_amount)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </td></tr>
);

const InvoiceRow = ({ g, open, onToggle }) => (
  <>
    <tr className={`border-b border-slate-100 cursor-pointer hover:bg-sky-50 ${g.status ? "bg-amber-50/40" : ""}`} onClick={onToggle} data-testid={`sales-tx-invoice-${g.billing_document}`}>
      <td className="py-1.5 px-1">{open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}</td>
      <td className="px-1 whitespace-nowrap">{dmy(g.billing_date)}</td>
      <td className="px-1 font-mono">{g.billing_document}<div className="text-[10px] text-slate-400">{g.odn}</div></td>
      <td className="px-1">{g.billing_type} · {g.type_desc} <StatusTag s={g.status} /></td>
      <td className="px-1 max-w-[260px]"><div className="truncate" title={g.customer_name}>{g.customer_name}</div><div className="text-[10px] text-slate-400 font-mono">{g.customer} · {g.state || g.country}</div></td>
      <td className="px-1 font-mono text-[11px] max-w-[160px] truncate" title={g.wbs}>{g.wbs}</td>
      <td className="px-1 text-right">{g.lines.length}</td>
      <td className={`px-1 text-right tabular-nums font-semibold ${g.sales_value < 0 ? "text-rose-700" : ""}`}>{inr(g.sales_value)}</td>
      <td className="px-1 text-right tabular-nums">{inr(g.tax_amount)}</td>
    </tr>
    {open && <Lines lines={g.lines} />}
  </>
);

export const SalesTransactions = ({ params }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState({});
  const [limit, setLimit] = useState(PAGE);
  const [dl, setDl] = useState(false);
  useEffect(() => {
    const ctrl = new AbortController();
    setBusy(true); setOpen({}); setLimit(PAGE);
    axios.get(`${API}/sales/transactions`, { params, signal: ctrl.signal }).then((r) => setData(r.data))
      .catch((e) => { if (!axios.isCancel(e)) toast.error(e.response?.data?.detail || "Could not load transactions"); }).finally(() => setBusy(false));
    return () => ctrl.abort();
  }, [params]);
  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!data || !s) return data?.invoices || [];
    return data.invoices.filter((g) => [g.billing_document, g.odn, g.customer, g.customer_name, g.wbs, g.status].some((v) => (v || "").toLowerCase().includes(s))
      || g.lines.some((l) => [l.material, l.material_description, l.delivery_no].some((v) => (v || "").toLowerCase().includes(s))));
  }, [data, q]);
  const download = async () => {
    setDl(true);
    try {
      const r = await axios.get(`${API}/sales/transactions/export`, { params, responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `sales_transactions_${params.from_date}_${params.to_date}.xlsx`; a.click();
    } catch { toast.error("Download failed"); } finally { setDl(false); }
  };
  const t = data?.totals;
  return (
    <Card className="border-slate-200 shadow-sm" data-testid="sales-transactions-card">
      <CardHeader className="pb-2 flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2"><Receipt className="h-4 w-4 text-sky-700" />Transactions — invoices and lines</CardTitle>
          <p className="text-xs text-slate-500 mt-0.5" data-testid="sales-tx-summary">
            {t ? `${t.invoices} documents · ${t.lines} lines · sales ${inr(t.sales_value)} · tax ${inr(t.tax_amount)}` : "Loading…"}{t?.cancel_lines ? ` · ${t.cancel_lines} cancelled / cancellation lines (net ${inr(t.cancel_value)}) shaded` : ""}. Follows the dates, filters and every chart or table click above — click a document to see its lines.
            {busy && data && <span className="ml-2 inline-flex items-center gap-1 text-sky-700" data-testid="sales-tx-refreshing"><Loader2 className="h-3 w-3 animate-spin" />Updating…</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative"><Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Invoice, customer, material…" className="h-8 w-60 pl-7 text-xs" data-testid="sales-tx-search" /></div>
          <Button size="sm" variant="outline" onClick={download} disabled={dl} data-testid="sales-tx-download-btn">{dl ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}Excel — all transactions</Button>
        </div>
      </CardHeader>
      <CardContent>
        {busy && !data ? <p className="text-xs text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading transactions…</p> : (
          <div className="max-h-[560px] overflow-auto">
            <table className="w-full text-xs" data-testid="sales-tx-table">
              <thead className="sticky top-0 bg-white z-10"><tr className="text-slate-500 uppercase text-[10px] border-b">{["", "Date", "Document", "Type", "Customer", "WBS", "Lines", "Sales ex-tax", "Tax"].map((h, i) => <th key={i} className={`py-1 px-1 ${i > 5 ? "text-right" : "text-left"}`}>{h}</th>)}</tr></thead>
              <tbody>
                {rows.slice(0, limit).map((g) => <Fragment key={g.billing_document}><InvoiceRow g={g} open={!!open[g.billing_document]} onToggle={() => setOpen((o) => ({ ...o, [g.billing_document]: !o[g.billing_document] }))} /></Fragment>)}
                {!rows.length && <tr><td colSpan={9} className="text-center text-slate-400 py-6">No transactions</td></tr>}
              </tbody>
            </table>
            {rows.length > limit && <Button variant="ghost" size="sm" className="mt-2" onClick={() => setLimit((n) => n + PAGE)} data-testid="sales-tx-more">Show more ({rows.length - limit} left)</Button>}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
